import { abortDocumentReaderUpload } from '@commenda/api/commenda-ai/document-reader/abortDocumentReaderUpload';
import { appendDocumentReaderUploadFiles } from '@commenda/api/commenda-ai/document-reader/appendDocumentReaderUploadFiles';
import { completeDocumentReaderUploadFile } from '@commenda/api/commenda-ai/document-reader/completeDocumentReaderUploadFile';
import { createDocumentReaderUpload } from '@commenda/api/commenda-ai/document-reader/createDocumentReaderUpload';
import { getDocumentReaderUploadBatch } from '@commenda/api/commenda-ai/document-reader/getDocumentReaderUploadBatch';
import { putDocumentReaderPart } from '@commenda/api/commenda-ai/document-reader/putDocumentReaderPart';
import { removeDocumentReaderUploadFile } from '@commenda/api/commenda-ai/document-reader/removeDocumentReaderUploadFile';
import { requestDocumentReaderPartUrls } from '@commenda/api/commenda-ai/document-reader/requestDocumentReaderPartUrls';
import {
  DocumentReaderUploadFileStatus,
  DocumentReaderUploadStatus,
  type TPutDocumentReaderPartResult,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';

import { DataRoomResumeRecordHelper } from './DataRoomResumeRecordHelper';
import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import type {
  IAddDataRoomDropParams,
  IAppendDataRoomParams,
  ICreateDataRoomParams,
  IDataRoomFileOutcome,
  IDataRoomFileProgress,
  IDataRoomLocalFile,
  IDataRoomResumeRecord,
  IDataRoomTransferContext,
  IDataRoomUploadSession,
  TAppendDataRoomResult,
  TCreateDataRoomResult,
  TDataRoomLimiter,
  TLoadDataRoomResumeResult,
  TRemoveDataRoomFileResult,
} from './types';

interface IDataRoomAppendState {
  files: IDataRoomLocalFile[];
  skippedEmptyFileNames: string[];
  error: string | null;
}

interface IDataRoomResumeMatchResult {
  files: IDataRoomLocalFile[];
  storedRows: IDataRoomFileProgress[];
  unmatchedNames: string[];
  abortedNames: string[];
  pendingRemovalFileIds: string[];
}

type TPartOutcome = { entityTag: string } | { error: string; isFatal: boolean };

const CANCELLED_ERROR = 'The upload was cancelled.';
const REMOVE_ERROR = 'Could not remove this document.';
const PAIRING_ERROR =
  'The server answered with a different file list, so the upload was stopped.';

/** The network side of a data-room upload: create, part URLs, parallel PUTs with retries, completion
 * and resume. Holds no state of its own; the hook owns the files and the progress. */
export class DataRoomUploadTransfer {
  static createLimiter(max: number): TDataRoomLimiter {
    let active = 0;
    const waiting: (() => void)[] = [];
    return async <T>(task: () => Promise<T>): Promise<T> => {
      if (active < max) active += 1;
      else
        await new Promise<void>((resolve) => {
          waiting.push(resolve);
        });
      try {
        return await task();
      } finally {
        // The slot passes straight to the next waiter, so a new caller can never slip in between.
        const next = waiting.shift();
        if (next) next();
        else active -= 1;
      }
    };
  }

  static async runPool<T>(
    items: readonly T[],
    limit: number,
    worker: (item: T) => Promise<void>
  ): Promise<void> {
    let nextIndex = 0;
    const lane = async (): Promise<void> => {
      if (nextIndex >= items.length) return;
      const item = items[nextIndex];
      nextIndex += 1;
      await worker(item);
      await lane();
    };
    await Promise.all(
      Array.from({ length: Math.min(limit, items.length) }, () => lane())
    );
  }

  /** Creates the upload with the first batch, then appends the rest; the resume record is written
   * after every batch. Any failure part way aborts the upload so nothing is left half open. */
  static async createRoom({
    uploads,
    limits,
    conversationId,
    companyId,
    signal,
    onRecordChange,
  }: Readonly<ICreateDataRoomParams>): Promise<TCreateDataRoomResult> {
    const firstBatch = uploads.slice(0, limits.maxFilesPerRequest);
    if (firstBatch.length === 0)
      return { success: false, error: 'There are no files to upload.' };

    const created = await createDocumentReaderUpload(
      {
        // Left out, never null: the backend checks any conversation it is sent.
        ...(conversationId ? { conversationId } : {}),
        ...(companyId ? { companyId } : {}),
        files: firstBatch.map((upload) =>
          DataRoomUploadHelper.toFileInput(upload)
        ),
      },
      signal
    );
    if (!created.success || !created.data)
      return {
        success: false,
        error: created.error ?? 'Could not start the document upload.',
      };

    const uploadId = created.data.upload.id;
    const firstFiles = DataRoomUploadHelper.pairCreatedFiles(
      firstBatch,
      created.data.upload
    );
    if (!firstFiles)
      return DataRoomUploadTransfer.stopCreate(uploadId, PAIRING_ERROR);

    const room: { record: IDataRoomResumeRecord } = {
      record: {
        uploadId,
        fileCount: uploads.length,
        lastModifiedByBatch: [firstBatch.map(({ file }) => file.lastModified)],
        removedFileIds: [],
      },
    };
    onRecordChange(room.record);
    const appended = await DataRoomUploadTransfer.appendBatches({
      uploadId,
      uploads: uploads.slice(firstBatch.length),
      limits,
      signal,
      onBatchAdded: (batchIndex, lastModified) => {
        room.record = DataRoomResumeRecordHelper.withBatch(
          room.record,
          batchIndex,
          lastModified
        );
        onRecordChange(room.record);
      },
    });
    if (appended.success === false)
      return DataRoomUploadTransfer.stopCreate(uploadId, appended.error);
    return {
      success: true,
      uploadId,
      files: [...firstFiles, ...appended.files],
      skippedEmptyFileNames: [
        ...created.data.upload.skippedEmptyFileNames,
        ...appended.skippedEmptyFileNames,
      ],
    };
  }

  /** Adds files to an open upload in batches, one request at a time; reports each batch once added. */
  static async appendBatches({
    uploadId,
    uploads,
    limits,
    signal,
    onBatchAdded,
  }: Readonly<IAppendDataRoomParams>): Promise<TAppendDataRoomResult> {
    const added: IDataRoomAppendState = {
      files: [],
      skippedEmptyFileNames: [],
      error: null,
    };
    await DataRoomUploadHelper.splitBatches(
      uploads,
      limits.maxFilesPerRequest
    ).reduce(async (previous, batch) => {
      await previous;
      if (added.error) return;
      const appended = await appendDocumentReaderUploadFiles(
        uploadId,
        batch.map((upload) => DataRoomUploadHelper.toFileInput(upload)),
        signal
      );
      const files =
        appended.success && appended.data
          ? DataRoomUploadHelper.pairCreatedFiles(batch, appended.data.upload)
          : null;
      if (!appended.data || !files) {
        added.error = appended.error ?? PAIRING_ERROR;
        return;
      }
      added.files = [...added.files, ...files];
      added.skippedEmptyFileNames = [
        ...added.skippedEmptyFileNames,
        ...appended.data.upload.skippedEmptyFileNames,
      ];
      onBatchAdded(
        appended.data.upload.batchIndex,
        batch.map(({ file }) => file.lastModified)
      );
    }, Promise.resolve());

    const { files, skippedEmptyFileNames, error } = added;
    return error
      ? { success: false, error, files, skippedEmptyFileNames }
      : { success: true, files, skippedEmptyFileNames };
  }

  /** Adds a later drop to the session's upload and keeps the resume record in step: the whole drop
   * counts from the start, and the files not added are taken off again. Null when the room stopped. */
  static async addDrop({
    session,
    uploads,
    limits,
    updateRecord,
  }: Readonly<IAddDataRoomDropParams>): Promise<TAppendDataRoomResult | null> {
    updateRecord((record) =>
      DataRoomResumeRecordHelper.withDrop(record, uploads.length)
    );
    const appended = session.controller.signal.aborted
      ? null
      : await DataRoomUploadTransfer.appendBatches({
          uploadId: session.uploadId,
          uploads,
          limits,
          signal: session.controller.signal,
          onBatchAdded: (batchIndex, lastModified) =>
            updateRecord((record) =>
              DataRoomResumeRecordHelper.withBatch(
                record,
                batchIndex,
                lastModified
              )
            ),
        });
    const addedCount = appended?.files.length ?? 0;
    if (addedCount < uploads.length)
      updateRecord((record) =>
        DataRoomResumeRecordHelper.withDrop(record, addedCount - uploads.length)
      );
    return appended;
  }

  /** Stops the file's parts in flight first, then removes it on the server. */
  static async removeFile(
    uploadId: string,
    fileId: string,
    fileController: AbortController | null
  ): Promise<TRemoveDataRoomFileResult> {
    fileController?.abort();
    const removed = await removeDocumentReaderUploadFile(uploadId, fileId);
    return removed.success && removed.data
      ? { success: true, upload: removed.data.upload }
      : { success: false, error: removed.error ?? REMOVE_ERROR };
  }

  /** Removes files again after a reload; the first refusal's message, or null. */
  static async removeFiles(
    uploadId: string,
    fileIds: readonly string[]
  ): Promise<string | null> {
    const results = await Promise.all(
      fileIds.map((fileId) =>
        DataRoomUploadTransfer.removeFile(uploadId, fileId, null)
      )
    );
    const refused = results.find((result) => result.success === false);
    return refused && refused.success === false ? refused.error : null;
  }

  static createSession(
    uploadId: string,
    files: IDataRoomLocalFile[],
    controller: AbortController,
    isMissingFiles: boolean
  ): IDataRoomUploadSession {
    return {
      uploadId,
      files,
      completedFileIds: new Set(),
      controller,
      limitPart: DataRoomUploadTransfer.createLimiter(
        DataRoomUploadHelper.PARTS_IN_FLIGHT
      ),
      fileControllers: new Map(),
      removingFileIds: new Set(),
      isMissingFiles,
    };
  }

  /** The controller each file runs under; a file whose controller was aborted (a refused removal)
   * gets a new one, so a retry runs again. */
  static linkFileControllers(
    session: IDataRoomUploadSession,
    files: readonly IDataRoomLocalFile[]
  ): Map<string, AbortController> {
    return new Map(
      files.map((file) => {
        const current = session.fileControllers.get(file.fileId);
        const controller =
          current && !current.signal.aborted
            ? current
            : DataRoomUploadTransfer.linkedController(
                session.controller.signal
              );
        session.fileControllers.set(file.fileId, controller);
        return [file.fileId, controller];
      })
    );
  }

  static forgetFile(session: IDataRoomUploadSession, fileId: string): void {
    const index = session.files.findIndex((file) => file.fileId === fileId);
    if (index >= 0) session.files.splice(index, 1);
    session.completedFileIds.delete(fileId);
    session.fileControllers.delete(fileId);
  }

  /** A controller that also aborts when its parent does: one per file, under the room's. */
  static linkedController(parent: AbortSignal): AbortController {
    const controller = new AbortController();
    if (parent.aborted) controller.abort();
    else
      parent.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    return controller;
  }

  /** Uploads files a few at a time and reports each as it settles. */
  static async uploadFiles(
    context: Readonly<IDataRoomTransferContext>,
    files: readonly IDataRoomLocalFile[],
    onFileSettled: (
      file: IDataRoomLocalFile,
      outcome: IDataRoomFileOutcome
    ) => void
  ): Promise<void> {
    await DataRoomUploadTransfer.runPool(
      files,
      DataRoomUploadHelper.PARTS_IN_FLIGHT,
      async (file) => {
        const signal = context.signalFor?.(file.fileId) ?? context.signal;
        if (signal.aborted) return;
        onFileSettled(
          file,
          await DataRoomUploadTransfer.uploadFile({ ...context, signal }, file)
        );
      }
    );
  }

  /** Sends only the parts S3 does not hold yet, then completes the file with its part list. */
  static async uploadFile(
    context: Readonly<IDataRoomTransferContext>,
    file: IDataRoomLocalFile
  ): Promise<IDataRoomFileOutcome> {
    const progress = {
      storedBytes: DataRoomUploadHelper.storedBytes(file),
      inFlight: new Map<number, number>(),
    };
    const report = (partNumber: number, loadedBytes: number) => {
      if (loadedBytes > 0) progress.inFlight.set(partNumber, loadedBytes);
      else progress.inFlight.delete(partNumber);
      const inFlightBytes = [...progress.inFlight.values()].reduce(
        (sum, bytes) => sum + bytes,
        0
      );
      context.onFileBytes(file.fileId, progress.storedBytes + inFlightBytes);
    };
    const failure: { outcome: { error: string; isFatal: boolean } | null } = {
      outcome: null,
    };
    const isStopped = () =>
      context.signal.aborted || failure.outcome?.isFatal === true;

    report(0, 0);
    const chunks = DataRoomUploadHelper.splitBatches(
      DataRoomUploadHelper.missingPartNumbers(file),
      context.maxPartUrlsPerRequest
    );
    await chunks.reduce(async (previous, partNumbers) => {
      await previous;
      if (isStopped()) return;
      const urls = await requestDocumentReaderPartUrls(
        context.uploadId,
        file.fileId,
        partNumbers,
        context.signal
      );
      if (!urls.success || !urls.data) {
        failure.outcome = failure.outcome ?? {
          error: urls.error ?? 'Could not prepare this file.',
          isFatal: false,
        };
        return;
      }
      const urlByPart = new Map(
        urls.data.partUrls.map((partUrl) => [partUrl.partNumber, partUrl.url])
      );
      await DataRoomUploadTransfer.runPool(
        partNumbers,
        DataRoomUploadHelper.PARTS_IN_FLIGHT_PER_FILE,
        async (partNumber) => {
          if (isStopped()) return;
          const outcome = await DataRoomUploadTransfer.sendPart(
            context,
            file,
            partNumber,
            urlByPart.get(partNumber) ?? null,
            0,
            report
          );
          if ('entityTag' in outcome) {
            file.storedEntityTags.set(partNumber, outcome.entityTag);
            const range = DataRoomUploadHelper.partRange(
              partNumber,
              file.selectedFile.file.size,
              file.partSizeBytes
            );
            progress.storedBytes += range.end - range.start;
            report(partNumber, 0);
          } else if (!failure.outcome || outcome.isFatal)
            failure.outcome = outcome;
        }
      );
    }, Promise.resolve());

    if (context.signal.aborted)
      return {
        success: false,
        isFatal: true,
        uploadStatus: null,
        error: CANCELLED_ERROR,
      };
    if (failure.outcome)
      return {
        success: false,
        isFatal: failure.outcome.isFatal,
        uploadStatus: null,
        error: failure.outcome.error,
      };

    const parts = [...file.storedEntityTags.entries()]
      .sort(([first], [second]) => first - second)
      .map(([partNumber, entityTag]) => ({ partNumber, entityTag }));
    const completed = await completeDocumentReaderUploadFile(
      context.uploadId,
      file.fileId,
      parts,
      context.signal
    );
    if (!completed.success || !completed.data) {
      // The backend's part list disagrees with ours, so a retry starts from what S3 says it holds.
      if (completed.status === 400)
        await DataRoomUploadTransfer.reloadStoredParts(context, file);
      return {
        success: false,
        isFatal: false,
        uploadStatus: null,
        error: completed.error ?? 'Could not finish this file.',
      };
    }
    return {
      success: true,
      isFatal: false,
      uploadStatus: completed.data.upload.status,
      error: null,
    };
  }

  /** Reads every batch of an unfinished upload and pairs the server's files with the re-selected ones. */
  static async loadResume(
    record: Readonly<IDataRoomResumeRecord>,
    uploads: readonly CommendaAiUploadFile[],
    signal: AbortSignal
  ): Promise<TLoadDataRoomResumeResult> {
    const first = await getDocumentReaderUploadBatch(
      record.uploadId,
      0,
      signal
    );
    if (!first.success || !first.data)
      return {
        success: false,
        status: null,
        error: first.error ?? 'Could not read the document upload.',
      };
    const { status, fileCount, batchCount } = first.data.upload;
    const removedFileIds = new Set(record.removedFileIds);
    if (status === DocumentReaderUploadStatus.ABORTED)
      return {
        success: false,
        status,
        error:
          'This upload was cancelled, so it cannot be finished. Upload the documents again.',
      };
    if (fileCount < DataRoomResumeRecordHelper.expectedFileCount(record))
      return {
        success: false,
        status,
        error:
          'This upload stopped before all its files were added. Cancel it and upload the documents again.',
      };

    const result: IDataRoomResumeMatchResult = {
      files: [],
      storedRows: [],
      unmatchedNames: [],
      abortedNames: [],
      pendingRemovalFileIds: [],
    };
    const batchIndexes = Array.from(
      { length: batchCount },
      (_, index) => index
    );
    const failure: { error: string | null } = { error: null };
    await batchIndexes.reduce(async (previous, batchIndex) => {
      await previous;
      if (failure.error) return;
      const batch =
        batchIndex === 0
          ? first
          : await getDocumentReaderUploadBatch(
              record.uploadId,
              batchIndex,
              signal
            );
      if (!batch.success || !batch.data) {
        failure.error = batch.error ?? 'Could not read the document upload.';
        return;
      }
      const serverFiles = batch.data.upload.files.filter((serverFile) => {
        if (!removedFileIds.has(serverFile.id)) return true;
        result.pendingRemovalFileIds.push(serverFile.id);
        return false;
      });
      const pending = serverFiles.filter(
        (serverFile) =>
          serverFile.status === DocumentReaderUploadFileStatus.UPLOADING
      );
      const match = DataRoomUploadHelper.matchResumeFiles(
        uploads,
        pending,
        batchIndex,
        record.lastModifiedByBatch[batchIndex]
      );
      result.files.push(...match.files);
      result.unmatchedNames.push(...match.unmatchedNames);
      serverFiles.forEach((serverFile) => {
        if (serverFile.status === DocumentReaderUploadFileStatus.COMPLETED)
          result.storedRows.push(DataRoomUploadHelper.toStoredRow(serverFile));
        if (serverFile.status === DocumentReaderUploadFileStatus.ABORTED)
          result.abortedNames.push(
            serverFile.relativePath ?? serverFile.fileName
          );
      });
    }, Promise.resolve());

    if (failure.error) return { success: false, status, error: failure.error };
    return { success: true, status, ...result };
  }

  static async fetchUploadStatus(
    uploadId: string
  ): Promise<DocumentReaderUploadStatus | null> {
    const read = await getDocumentReaderUploadBatch(uploadId, 0);
    return read.success && read.data ? read.data.upload.status : null;
  }

  /** Replaces the file's part list with the parts S3 holds; if the batch cannot be read, every part
   * is sent again. */
  private static async reloadStoredParts(
    context: Readonly<IDataRoomTransferContext>,
    file: IDataRoomLocalFile
  ): Promise<void> {
    const batch = await getDocumentReaderUploadBatch(
      context.uploadId,
      file.batchIndex,
      context.signal
    );
    const serverFile = batch.data?.upload.files.find(
      (candidate) => candidate.id === file.fileId
    );
    file.storedEntityTags.clear();
    serverFile?.uploadedParts.forEach((part) =>
      file.storedEntityTags.set(part.partNumber, part.entityTag)
    );
  }

  private static async stopCreate(
    uploadId: string,
    error: string
  ): Promise<TCreateDataRoomResult> {
    await abortDocumentReaderUpload(uploadId);
    return { success: false, error };
  }

  /** One part with up to three retries, each after a short wait (and after the browser is back
   * online) with a fresh URL. A 2xx without an ETag is never retried: the bucket CORS hides it. */
  private static async sendPart(
    context: Readonly<IDataRoomTransferContext>,
    file: Readonly<IDataRoomLocalFile>,
    partNumber: number,
    url: string | null,
    attempt: number,
    report: (partNumber: number, loadedBytes: number) => void
  ): Promise<TPartOutcome> {
    const range = DataRoomUploadHelper.partRange(
      partNumber,
      file.selectedFile.file.size,
      file.partSizeBytes
    );
    const result: TPutDocumentReaderPartResult = url
      ? await context.limitPart(() =>
          putDocumentReaderPart({
            url,
            blob: file.selectedFile.file.slice(range.start, range.end),
            signal: context.signal,
            onProgress: (loadedBytes) => report(partNumber, loadedBytes),
          })
        )
      : {
          success: false,
          status: 0,
          error: `Could not get a new link for part ${partNumber}.`,
        };
    report(partNumber, 0);

    if (result.success === true) return { entityTag: result.entityTag };
    if (context.signal.aborted)
      return { error: CANCELLED_ERROR, isFatal: true };
    if (result.status >= 200 && result.status < 300)
      return { error: result.error, isFatal: true };

    const delayMs = DataRoomUploadHelper.RETRY_DELAYS_MS[attempt];
    if (delayMs === undefined) return { error: result.error, isFatal: false };
    await DataRoomUploadTransfer.wait(delayMs, context.signal);
    await DataRoomUploadTransfer.waitUntilOnline(context.signal);
    if (context.signal.aborted)
      return { error: CANCELLED_ERROR, isFatal: true };

    const fresh = await requestDocumentReaderPartUrls(
      context.uploadId,
      file.fileId,
      [partNumber],
      context.signal
    );
    const freshUrl =
      fresh.data?.partUrls.find((partUrl) => partUrl.partNumber === partNumber)
        ?.url ?? null;
    return DataRoomUploadTransfer.sendPart(
      context,
      file,
      partNumber,
      freshUrl,
      attempt + 1,
      report
    );
  }

  private static wait(delayMs: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, delayMs);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true }
      );
    });
  }

  /** An offline browser fails every PUT at once; waiting here keeps those failures from using up the
   * part's retries. */
  private static waitUntilOnline(signal: AbortSignal): Promise<void> {
    if (typeof navigator === 'undefined' || navigator.onLine)
      return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        window.removeEventListener('online', done);
        resolve();
      };
      window.addEventListener('online', done);
      signal.addEventListener('abort', done, { once: true });
    });
  }
}
